import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Camera, Upload, X, Loader2, Check, Plus } from "lucide-react";
import { toast } from "sonner";
import { getStoredLang } from "../components/LanguagePicker";
import { t, useLang } from "../lib/i18n";

// "Add photo of library" flow:
//   1. User takes / uploads a photo
//   2. We ship it to the backend (Claude Sonnet vision) which returns a list of titles+authors
//   3. User confirms each book — we look up Open Library and add to library

export default function PhotoScanPage() {
  useLang();
  const navigate = useNavigate();
  const fileRef = useRef(null);         // gallery / files
  const cameraRef = useRef(null);       // camera capture
  const [previewUrl, setPreviewUrl] = useState("");
  const [scanning, setScanning] = useState(false);
  const [detected, setDetected] = useState(null); // [{title, author}]
  const [selected, setSelected] = useState(() => new Set());
  const [addingIdx, setAddingIdx] = useState(-1);
  const [added, setAdded] = useState(() => new Set());

  const pickFile = () => fileRef.current?.click();
  const pickCamera = () => cameraRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      toast.error(t("scan.pick_jpeg"));
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error(t("scan.too_large"));
      return;
    }
    // Downscale to keep the payload reasonable.
    const resized = await downscale(file, 1600);
    setPreviewUrl(URL.createObjectURL(file));
    setScanning(true);
    setDetected(null);
    setSelected(new Set());
    setAdded(new Set());
    try {
      const { data } = await api.post("/books/scan-photo", { image_base64: resized });
      const rows = data.detected || [];
      setDetected(rows);
      setSelected(new Set(rows.map((_, i) => i))); // select all by default
      if (!rows.length) toast.message(t("scan.cannot_identify"));
      else toast.success(`${rows.length} ${t("scan.detected_count")} · ${t("scan.confirm_below")}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setScanning(false);
    }
  };

  const toggle = (i) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(i)) next.delete(i); else next.add(i);
    return next;
  });

  const addAll = async () => {
    const picks = Array.from(selected).map((i) => detected[i]).filter(Boolean);
    if (!picks.length) return;
    let ok = 0, fail = 0;
    for (let idx = 0; idx < picks.length; idx++) {
      const item = picks[idx];
      const realIdx = detected.findIndex((d) => d === item);
      setAddingIdx(realIdx);
      const res = await addOne(item);
      if (res) { ok++; setAdded((prev) => new Set(prev).add(realIdx)); }
      else fail++;
    }
    setAddingIdx(-1);
    if (ok) toast.success(`${ok} ${t("scan.added_ok")}`);
    if (fail) toast.message(`${fail} ${t("scan.not_matched")}`);
    if (ok) setTimeout(() => navigate("/library"), 1200);
  };

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-12 fade-up" data-testid="photo-scan-page">
      <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#0A0A0A] mb-3 inline-flex items-center gap-2">
        <Camera className="w-3.5 h-3.5" strokeWidth={1.5} /> {t("scan.eyebrow_short")}
      </div>
      <h1 className="font-heading text-4xl md:text-5xl text-[#0A0A0A] tracking-tight">{t("scan.headline")}</h1>
      <p className="text-[#4A4030] mt-3 max-w-xl">{t("scan.long_subtitle")}</p>

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={onFile}
        data-testid="photo-file-input"
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onFile}
        data-testid="photo-camera-input"
      />

      {!previewUrl && (
        <div className="mt-10 border border-dashed border-[rgba(0,0,0,0.18)] p-16 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <button onClick={pickCamera} className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-6 py-3 rounded-sm transition-colors" data-testid="photo-take">
              <Camera className="w-3.5 h-3.5" strokeWidth={1.5} /> {t("scan.take_photo")}
            </button>
            <button onClick={pickFile} className="inline-flex items-center gap-2 border border-[rgba(10,10,10,0.18)] bg-[#FAF5E6] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-xs px-6 py-3 rounded-sm transition-colors" data-testid="photo-upload">
              <Upload className="w-3.5 h-3.5" strokeWidth={1.5} /> {t("scan.upload_btn")}
            </button>
          </div>
          <p className="text-xs text-[#8C7F65] font-mono uppercase tracking-widest">{t("scan.file_hint")}</p>
        </div>
      )}

      {previewUrl && (
        <div className="mt-10 grid md:grid-cols-2 gap-8">
          <div className="relative bg-[#E8DFC1] border border-[rgba(0,0,0,0.10)]">
            <img src={previewUrl} alt="Uploaded shelf" className="w-full h-auto object-contain" />
            <button
              onClick={() => { setPreviewUrl(""); setDetected(null); setSelected(new Set()); setAdded(new Set()); }}
              className="absolute top-2 right-2 p-1.5 bg-[#F2EAD3] hover:bg-white rounded-sm shadow"
              aria-label="Remove"
              data-testid="photo-remove"
            >
              <X className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
          </div>
          <div>
            {scanning && (
              <div className="border border-dashed border-[rgba(0,0,0,0.12)] p-10 text-center">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3 text-[#525252]" strokeWidth={1.5} />
                <div className="font-mono uppercase tracking-widest text-xs text-[#525252]">{t("scan.reading_spines")}</div>
                <p className="text-xs text-[#8C7F65] mt-3">{t("scan.can_take")}</p>
              </div>
            )}
            {!scanning && detected && detected.length === 0 && (
              <div className="border border-dashed border-[rgba(0,0,0,0.12)] p-10 text-center">
                <div className="font-heading text-xl text-[#525252]">{t("scan.none_detected")}</div>
                <p className="text-sm text-[#8C7F65] mt-2">{t("scan.try_clearer")}</p>
              </div>
            )}
            {!scanning && detected && detected.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-mono uppercase tracking-widest text-[10px] text-[#525252]" data-testid="photo-detected-count">
                    {detected.length} {t("scan.detected_count")} · {selected.size} {t("recs.selected")}
                  </div>
                  <button
                    onClick={() => setSelected(selected.size === detected.length ? new Set() : new Set(detected.map((_, i) => i)))}
                    className="font-mono uppercase tracking-widest text-[10px] text-[#0A0A0A] hover:underline"
                    data-testid="photo-toggle-all"
                  >
                    {selected.size === detected.length ? t("scan.deselect_all") : t("scan.select_all")}
                  </button>
                </div>
                <ul className="space-y-1.5" data-testid="photo-detected-list">
                  {detected.map((d, i) => {
                    const isDone = added.has(i);
                    const isBusy = addingIdx === i;
                    return (
                      <li key={i}>
                        <label className={`flex items-start gap-3 p-3 border transition-colors cursor-pointer ${isDone ? "border-[rgba(0,0,0,0.10)] bg-[#E8DFC1] opacity-60" : selected.has(i) ? "border-[#0A0A0A] bg-[#FAF5E6]" : "border-[rgba(0,0,0,0.10)] bg-[#F2EAD3] hover:bg-[#FAF5E6]"}`}>
                          <input
                            type="checkbox"
                            checked={selected.has(i)}
                            onChange={() => toggle(i)}
                            disabled={isDone || isBusy}
                            className="mt-0.5"
                            data-testid={`photo-item-${i}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-[#0A0A0A] leading-tight">{d.title}</div>
                            {d.author && <div className="text-xs text-[#525252] mt-0.5">{t("scan.by")} {d.author}</div>}
                          </div>
                          {isDone && <Check className="w-4 h-4 text-[#0A0A0A]" strokeWidth={2} />}
                          {isBusy && <Loader2 className="w-4 h-4 animate-spin text-[#525252]" strokeWidth={1.5} />}
                        </label>
                      </li>
                    );
                  })}
                </ul>
                <div className="mt-6 flex items-center justify-end gap-3">
                  <Link to="/library" className="font-mono uppercase tracking-widest text-[10px] text-[#525252] hover:text-[#0A0A0A] px-3 py-2">{t("recs.cancel")}</Link>
                  <button
                    onClick={addAll}
                    disabled={!selected.size || addingIdx >= 0}
                    className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-5 py-3 rounded-sm transition-colors disabled:opacity-40"
                    data-testid="photo-add-selected"
                  >
                    {addingIdx >= 0 ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} /> : <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />} {t("scan.add_n")} {selected.size} {t("scan.to_library")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Look up a detected book on Open Library then POST /books.
async function addOne(item) {
  const lang = getStoredLang();
  const fields = "key,title,author_name,first_publish_year,cover_i,number_of_pages_median,subject";
  const candidates = [
    { title: item.title, author: item.author },
    { q: `${item.title} ${item.author || ""}`.trim() },
    { title: item.title },
  ];
  let doc = null;
  for (const c of candidates) {
    const params = new URLSearchParams({ ...c, limit: "3", fields, language: lang });
    try {
      const res = await fetch(`https://openlibrary.org/search.json?${params}`);
      const data = await res.json();
      doc = data?.docs?.[0];
      if (doc?.key) break;
    } catch { /* try next */ }
  }
  if (!doc?.key) return false;
  try {
    await api.post("/books", {
      title: doc.title || item.title,
      author: (doc.author_name && doc.author_name[0]) || item.author || "Unknown",
      cover_url: doc.cover_i ? `https://covers.openlibrary.org/b/id/${doc.cover_i}-L.jpg` : "",
      genre: "Fiction",
      age_rating: "All Ages",
      year: doc.first_publish_year || null,
      pages: doc.number_of_pages_median || null,
      description: "",
      openlibrary_key: doc.key,
    });
    return true;
  } catch {
    return false;
  }
}

// Resize + JPEG-encode client-side so we don't ship a 12 MB payload.
async function downscale(file, maxDim) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("image decode failed"));
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        // JPEG at 0.82 quality — good balance for text legibility.
        const dataUrl = canvas.toDataURL("image/jpeg", 0.82);
        resolve(dataUrl.split(",")[1]);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}
